// ---------------------------------------------------------------------------
// Shared string constants & lookup tables
// ---------------------------------------------------------------------------
// Centralizes repeated literals so they stay consistent across the app.
// Individual components can still define *component-specific* configs locally;
// this file is for values that appear in 2+ unrelated files.
// ---------------------------------------------------------------------------

// ---- Case statuses --------------------------------------------------------

export const CASE_STATUS = {
  ACTIVE: "active",
  PENDING: "pending",
  REVIEW: "review",
  IN_REVIEW: "in_review",
  CLOSED: "closed",
  ARCHIVED: "archived",
  URGENT: "urgent",
} as const;

export type CaseStatusValue = (typeof CASE_STATUS)[keyof typeof CASE_STATUS];

export const CASE_STATUS_CONFIG: Record<
  string,
  { label: string; color: "green" | "yellow" | "blue" | "red" | "orange" | "cyan" }
> = {
  [CASE_STATUS.ACTIVE]: { label: "Active", color: "green" },
  [CASE_STATUS.PENDING]: { label: "Pending", color: "yellow" },
  [CASE_STATUS.REVIEW]: { label: "In Review", color: "blue" },
  [CASE_STATUS.IN_REVIEW]: { label: "In Review", color: "blue" },
  [CASE_STATUS.CLOSED]: { label: "Closed", color: "cyan" },
  [CASE_STATUS.ARCHIVED]: { label: "Archived", color: "cyan" },
  [CASE_STATUS.URGENT]: { label: "Urgent", color: "red" },
};

export function getCaseStatusConfig(status: string) {
  return CASE_STATUS_CONFIG[status] ?? { label: status, color: "cyan" as const };
}

// ---- Evidence / processing statuses ---------------------------------------

export const EVIDENCE_STATUS = {
  PENDING: "pending",
  QUEUED: "queued",
  PROCESSING: "processing",
  COMPLETE: "complete",
  COMPLETED: "completed",
  VERIFIED: "verified",
  REJECTED: "rejected",
  FAILED: "failed",
} as const;

export type EvidenceStatusValue =
  (typeof EVIDENCE_STATUS)[keyof typeof EVIDENCE_STATUS];

export const EVIDENCE_STATUS_CONFIG: Record<
  string,
  { label: string; color: "green" | "cyan" | "yellow" | "red" }
> = {
  [EVIDENCE_STATUS.COMPLETE]: { label: "Complete", color: "green" },
  [EVIDENCE_STATUS.COMPLETED]: { label: "Complete", color: "green" },
  [EVIDENCE_STATUS.VERIFIED]: { label: "Verified", color: "green" },
  [EVIDENCE_STATUS.PROCESSING]: { label: "Processing", color: "cyan" },
  [EVIDENCE_STATUS.QUEUED]: { label: "Queued", color: "yellow" },
  [EVIDENCE_STATUS.PENDING]: { label: "Pending", color: "yellow" },
  [EVIDENCE_STATUS.REJECTED]: { label: "Rejected", color: "red" },
  [EVIDENCE_STATUS.FAILED]: { label: "Failed", color: "red" },
};

export function getEvidenceStatusConfig(status: string) {
  return (
    EVIDENCE_STATUS_CONFIG[status] ?? { label: status, color: "yellow" as const }
  );
}

// ---- Discovery item statuses ----------------------------------------------

export const DISCOVERY_STATUS = {
  NOT_REQUESTED: "not_requested",
  REQUESTED: "requested",
  RECEIVED: "received",
  REVIEWED: "reviewed",
  MISSING: "missing",
  REDACTED: "redacted",
  DISPUTED: "disputed",
  PRODUCED: "produced",
  OVERDUE: "overdue",
  PENDING: "pending",
} as const;

export type DiscoveryStatusValue =
  (typeof DISCOVERY_STATUS)[keyof typeof DISCOVERY_STATUS];

export const DISCOVERY_STATUS_CONFIG: Record<
  string,
  { label: string; color: "cyan" | "blue" | "green" | "yellow" | "red" | "orange" | "purple" }
> = {
  [DISCOVERY_STATUS.NOT_REQUESTED]: { label: "Not Requested", color: "cyan" },
  [DISCOVERY_STATUS.REQUESTED]: { label: "Requested", color: "blue" },
  [DISCOVERY_STATUS.RECEIVED]: { label: "Received", color: "green" },
  [DISCOVERY_STATUS.REVIEWED]: { label: "Reviewed", color: "green" },
  [DISCOVERY_STATUS.MISSING]: { label: "Missing", color: "red" },
  [DISCOVERY_STATUS.REDACTED]: { label: "Redacted", color: "orange" },
  [DISCOVERY_STATUS.DISPUTED]: { label: "Disputed", color: "purple" },
  [DISCOVERY_STATUS.PRODUCED]: { label: "Produced", color: "green" },
  [DISCOVERY_STATUS.OVERDUE]: { label: "Overdue", color: "red" },
  [DISCOVERY_STATUS.PENDING]: { label: "Pending", color: "yellow" },
};

export function getDiscoveryStatusConfig(status: string) {
  return (
    DISCOVERY_STATUS_CONFIG[status] ?? { label: status, color: "cyan" as const }
  );
}

// ---- Bail / tracking statuses ---------------------------------------------

export const TRACKING_STATUS = {
  ACTIVE: "active",
  COMPLIANT: "compliant",
  NON_COMPLIANT: "non_compliant",
  MISSING: "missing",
} as const;

export type TrackingStatusValue =
  (typeof TRACKING_STATUS)[keyof typeof TRACKING_STATUS];

// ---- Async job statuses ---------------------------------------------------

export const JOB_STATUS = {
  QUEUED: "queued",
  RUNNING: "running",
  COMPLETED: "completed",
  FAILED: "failed",
} as const;

export type JobStatusValue = (typeof JOB_STATUS)[keyof typeof JOB_STATUS];

// ---- Disclosure statuses --------------------------------------------------

export const DISCLOSURE_STATUS = {
  PENDING: "pending",
  DISCLOSED: "disclosed",
  WITHHELD: "withheld",
} as const;

export type DisclosureStatusValue =
  (typeof DISCLOSURE_STATUS)[keyof typeof DISCLOSURE_STATUS];

// ---- WebSocket event type strings -----------------------------------------

export const WS_EVENT = {
  RAID_ALERT: "raid_alert",
  ANALYSIS_COMPLETE: "analysis_complete",
  UPLOAD_PROGRESS: "upload_progress",
  TEAM_ACTIVITY: "team_activity",
  NOTIFICATION: "notification",
  JOB_PROGRESS: "job_progress",
  PRESENCE: "presence",
} as const;

export type WsEventValue = (typeof WS_EVENT)[keyof typeof WS_EVENT];

// ---- Evidence display type colors -----------------------------------------

export const EVIDENCE_TYPE_COLORS: Record<
  string,
  "cyan" | "purple" | "pink" | "green" | "orange"
> = {
  video: "cyan",
  audio: "purple",
  document: "pink",
  image: "green",
  other: "orange",
};

// ---- Notification severity ------------------------------------------------

export const SEVERITY = {
  INFO: "info",
  WARNING: "warning",
  ERROR: "error",
  SUCCESS: "success",
} as const;

export type SeverityValue = (typeof SEVERITY)[keyof typeof SEVERITY];

// ---- Local-storage keys ---------------------------------------------------

export const STORAGE_KEYS = {
  AUTH_STORE: "framecounsel-auth",
  LEGACY_TOKEN: "framecounsel_token",
} as const;

// ---- Auth cookie name -----------------------------------------------------

export const AUTH_COOKIE_NAME = "access_token";
